import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import {
  useInlineNotifications,
  useNotification,
  useSelection,
} from '../../../../hooks'
import { useAsyncTable } from '../../../Table'
import { getMassActionsSuccessItemsLength } from '../../DocFlow.utils'
import {
  checkDocFlowValidationErrorsModalEmptyCommunicationTypes,
  DOC_FLOW_SENDING_COMMUNICATION_TABLE_COMPOSITE_MASS_ACTION,
  DocFlowSendingFieldName,
  DocFlowSendingWidgetProcessFieldName,
  getDocFlowSendingResultNotificationTitle,
  getDocFlowValidationErrorsModalIdsLengthToSave,
  getDocFlowValidationErrorsModalNotification,
  getDocFlowValidationErrorsModalNotifications,
  getDocFlowValidationErrorsModalPreparedFilterItemsToBackend,
  getDocFlowValidationErrorsModalSubmitParams,
  getDocFlowValidationErrorsModalUpdatedSingleActionItems,
  getSendingTableApiUrls,
  hasDocFlowValidationErrorsModalChanges,
  isDocFlowValidationErrorsModalSendEpostOff,
  performDocFlowSendingCompositeMassAction,
} from '../../DocFlowSending'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowSendingContext } from '../useDocFlowSendingContext'

export function useDocFlowSendingEpostValidationErrorsReportModal({
  errorIds,
  onClose,
}) {
  const { t } = useTranslation()

  const { documentId } = useDocFlowContext()

  const { getFileProcess, handleUpdateEpostSettings, setPageLoader } =
    useDocFlowSendingContext()

  const [saveToProfile, setSaveToProfile] = useState(EMPTY_OBJECT)
  const [sendEmail, setSendEmail] = useState(EMPTY_OBJECT)
  const [sendAdditionalEmail, setSendAdditionalEmail] = useState(EMPTY_OBJECT)

  const [isCloseConfirm, setCloseConfirm] = useState(false)

  const [emptyCommunicationRecipients, setEmptyCommunicationRecipients] =
    useState(EMPTY_LIST)

  const initialNotifications = [getDocFlowValidationErrorsModalNotification(t)]

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications({ initialNotifications })

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const availableNotifications = getDocFlowValidationErrorsModalNotifications(t)

  const filterParams = errorIds ? { epost_error_code: errorIds } : null

  const tableData = useAsyncTable({
    ...getSendingTableApiUrls(documentId),
    filterParams,
    withoutSubMenu: true,
  })

  const { handleLoadInit, items, pagination } = tableData

  useEffect(() => {
    handleLoadInit()
  }, [])

  const selectionData = useSelection({
    data: items,
    pagination,
  })

  const { handleCleanSelection, selectAllPages, selected } = selectionData

  function handleCloseTableModal() {
    if (
      hasDocFlowValidationErrorsModalChanges({
        saveToProfile,
        selectAllPages,
        selected,
        sendAdditionalEmail,
        sendEmail,
      })
    ) {
      setCloseConfirm(true)

      return
    }

    onClose()
  }

  function handleToggleSaveToProfile({ item, value }) {
    setSaveToProfile(previous =>
      getDocFlowValidationErrorsModalUpdatedSingleActionItems({
        cellName: DocFlowSendingFieldName.SaveToProfile,
        item,
        previous,
        value,
      })
    )
  }

  function handleToggleSendEmail({ item, value }) {
    setSendEmail(previous =>
      getDocFlowValidationErrorsModalUpdatedSingleActionItems({
        cellName: DocFlowSendingFieldName.SendEmail,
        item,
        previous,
        value,
      })
    )
  }

  function handleToggleSendAdditionalEmail({ item, value }) {
    setSendAdditionalEmail(previous =>
      getDocFlowValidationErrorsModalUpdatedSingleActionItems({
        cellName: DocFlowSendingFieldName.SendAdditionalEmail,
        item,
        previous,
        value,
      })
    )
  }

  function handleSuccessAction({ action, data, totalCount }) {
    handleCleanSelection()

    const successItemsCount = getMassActionsSuccessItemsLength(data)

    const notificationHandler =
      successItemsCount === 0
        ? handleAddErrorNotification
        : handleAddSuccessNotification

    onClose()

    notificationHandler({
      title: getDocFlowSendingResultNotificationTitle({
        action,
        count: successItemsCount,
        t,
        totalCount,
      }),
    })

    setPageLoader(false)
  }

  function handleClickSubmit() {
    const {
      saveToProfilePreparedItems,
      sendAdditionalEmailPreparedItems,
      sendEmailPreparedItems,
    } = getDocFlowValidationErrorsModalPreparedFilterItemsToBackend({
      saveToProfile,
      sendAdditionalEmail,
      sendEmail,
    })

    const emptyCommunicationRecipientIds =
      checkDocFlowValidationErrorsModalEmptyCommunicationTypes({
        items: items.items,
        saveToProfile: saveToProfilePreparedItems,
        sendAdditionalEmail: sendAdditionalEmailPreparedItems,
        sendEmail: sendEmailPreparedItems,
      })

    setEmptyCommunicationRecipients(emptyCommunicationRecipientIds)

    if (emptyCommunicationRecipientIds.length > 0) {
      handleAddNotification(availableNotifications.emptyCommunications)

      return
    } else {
      handleRemoveNotification(availableNotifications.emptyCommunications.id)
    }

    const idsLegth = getDocFlowValidationErrorsModalIdsLengthToSave({
      items: items.items,
      saveToProfilePreparedItems,
      sendAdditionalEmailPreparedItems,
      sendEmailPreparedItems,
    })

    if (
      idsLegth === 0 &&
      isDocFlowValidationErrorsModalSendEpostOff(items.items)
    ) {
      onClose()

      return
    }

    const params = {
      action_to_filters: getDocFlowValidationErrorsModalSubmitParams({
        items: items.items,
        saveToProfile: saveToProfilePreparedItems,
        sendAdditionalEmail: sendAdditionalEmailPreparedItems,
        sendEmail: sendEmailPreparedItems,
      }),
    }

    const countOfItems = selectAllPages ? pagination.count : idsLegth

    setPageLoader(true)

    performDocFlowSendingCompositeMassAction(documentId, params)
      .then(({ data }) => {
        if (!isDocFlowValidationErrorsModalSendEpostOff(items.items)) {
          getFileProcess().then(response => {
            if (
              !response[
                DocFlowSendingWidgetProcessFieldName.IsEpostDisabledForAll
              ]
            ) {
              handleUpdateEpostSettings()
            }
          })
        }

        handleSuccessAction({
          action: DOC_FLOW_SENDING_COMMUNICATION_TABLE_COMPOSITE_MASS_ACTION,
          data,
          totalCount: countOfItems,
        })
      })
      .catch(() => {
        handleAddErrorNotification(
          'Notification.DocFlowEpostValidationErrorsCompositeMassActionErrorTitleKey'
        )

        setPageLoader(false)
      })
  }

  return {
    emptyCommunicationRecipients,
    handleClickSubmit,
    handleCloseTableModal,
    handleToggleSaveToProfile,
    handleToggleSendAdditionalEmail,
    handleToggleSendEmail,
    isCloseConfirm,
    notifications,
    saveToProfile,
    selectionData,
    sendAdditionalEmail,
    sendEmail,
    setCloseConfirm,
    tableData,
  }
}
